'use client';
import styles from './map.module.css';
import { Location } from '@scitrials-sst/core/Next/types';
import {
	GoogleMap,
	MarkerF,
	InfoWindow,
	useJsApiLoader,
} from '@react-google-maps/api';
import { useCallback, useState } from 'react';

const containerStyle = {
	width: '100%',
	height: '600px',
	borderRadius: '8px 8px 0 0',
	margin: '1rem 0',
};

const options = {
	mapTypeControl: false,
	zoomControl: false,
	fullScreenControl: false,
	clickableIcons: false,
	scrollWheel: true,
	streetViewControl: false,
};

const defaultCoord = {
	lat: 53.46212529487407,
	lon: -2.1172590530745725,
};

export default function Map({ Marks = [] }: { Marks: Location[] }) {
	if (Marks.length === 0) return null;

	const [selected, setSelected] = useState<null | Location>(null);

	const { isLoaded } = useJsApiLoader({
		googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!,
		libraries: ['marker'],
	});

	const [map, setMap] = useState<google.maps.Map | null>(null);

	const onLoad = useCallback(
		(map: google.maps.Map) => {
			if (!Marks || Marks.length === 0) return;

			if (Marks.length === 1) {
				map.setCenter({
					lat: Marks[0]?.geoPoint?.lat ?? defaultCoord.lat,
					lng: Marks[0]?.geoPoint?.lon ?? defaultCoord.lon,
				});
				map.setZoom(12);
			} else {
				const bounds = new window.google.maps.LatLngBounds();
				Marks.forEach((coord) => {
					if (coord?.geoPoint?.lat && coord?.geoPoint?.lon)
						return bounds.extend({
							lat: +coord?.geoPoint?.lat,
							lng: +coord?.geoPoint?.lon,
						});
				});
				map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
			}

			setMap(map);
		},
		[Marks]
	);

	const onUnmount = useCallback(function callback(map: google.maps.Map) {
		setMap(null);
	}, []);

	if (!isLoaded || !Marks || Marks.length === 0) return null;

	return (
		<GoogleMap
			mapContainerStyle={containerStyle}
			center={
				Marks && Marks?.length > 0
					? { lat: Marks[0].geoPoint.lat, lng: Marks[0].geoPoint.lon }
					: undefined
			}
			zoom={10}
			onLoad={onLoad}
			onUnmount={onUnmount}
			options={options}
		>
			{Marks.map((mark, index) => {
				if (mark?.geoPoint?.lat && mark?.geoPoint?.lon)
					return (
						<MarkerF
							key={index}
							position={{ lat: mark.geoPoint.lat, lng: mark.geoPoint.lon }}
							onClick={() => setSelected(mark)}
						/>
					);
			})}

			{selected && (
				<InfoWindow
					position={{
						lat: selected?.geoPoint?.lat ?? defaultCoord.lat,
						lng: selected?.geoPoint?.lon ?? defaultCoord.lon,
					}}
					onCloseClick={() => setSelected(null)}
				>
					<div className={styles.card}>
						<div>
							<p className={styles.facility}>{selected?.facility}</p>
							<p>{selected?.city}</p>
							<p>{selected?.state}</p>
							<p>{selected?.zip}</p>
							<p>{selected?.country}</p>
						</div>
					</div>
				</InfoWindow>
			)}
		</GoogleMap>
	);
}
